/* eslint-disable no-console */
import PasswordReset from 'pages/passwordReset/PasswordReset';
import SelectTenant from 'pages/selectTenant/SelectTenant';
import { useApplicationSubdomain } from 'queries/hooks/useApplicationSubdomain';
import { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { CURRENT_WORKSPACE } from 'services/WorkspaceService';
import { SignInFail } from 'ui/errorHandling/SignInFail';
import { AppSkeleton } from 'ui/wrappers/AppSkeleton';
import Auth from './';
import { findSubdomainTenantCorrection } from './findSubdomainTenantCorrection';
import { getSubdomainFromAppUrl } from './getApplicationSubdomain';

export default function RequireAuth({ children }) {
    const { data: applicationSubdomain, isLoading: isApplicationSubdomainLoading } = useApplicationSubdomain();

    const { tenantToSwitchTo, requireSelection } = findSubdomainTenantCorrection({
        subdomainTenantIds: applicationSubdomain?.tenants?.map((tenant) => tenant.tenantId),
        userTenants: Auth.user?.tenants,
        currentTenant: Auth.user?.tenant
    });

    useEffect(() => {
        if (tenantToSwitchTo) {
            // preferredTenant is read once at app load, so apply the correction by storing it and reloading.
            window.localStorage.setItem('preferredTenant', tenantToSwitchTo);
            window.localStorage.removeItem(CURRENT_WORKSPACE); // the remembered workspace belongs to the old tenant
            window.location.reload();
        }
    }, [tenantToSwitchTo]);

    /**
     * When the session has no preference yet, the subdomain auto-selects a tenant, so wait for the
     * subdomain lookup before resolving access. This avoids briefly rendering the wrong tenant and
     * then reloading once the auto-selection is known.
     */
    const isOnSubdomain = Boolean(getSubdomainFromAppUrl(Auth.getAppLoadUrl()));
    if (tenantToSwitchTo || (Auth.user?.tenants?.length && isOnSubdomain && isApplicationSubdomainLoading)) {
        return <AppSkeleton />;
    }

    // User is authenticated and has a tenant. An ambiguous subdomain with no preference yet requires a
    // choice, so fall through to tenant selection rather than rendering a tenant we haven't picked.
    if (Auth.user && Auth.user.tenant && !requireSelection) {
        return children;
    }

    // User is authenticated, and in multiple tenants, but has not chosen one
    // Show page to select tenant
    if (Auth.user && Auth.user.tenants) {
        return (
            <Routes>
                <Route path='/*' element={<SelectTenant signIn={true} />} />
            </Routes>
        );
    }

    // Show password reset page if user has reset their password
    if (Auth.user && Auth.user.passwordReset) {
        return (
            <Routes>
                <Route path='/*' element={<PasswordReset />} />
            </Routes>
        );
    }

    // User has authenticated but they don't have access to any tenants.
    if (Auth.user && getStandardTenants().length === 0) {
        return <SignInFail email={Auth.user.email} />;
    }

    Auth.login();

    return null;
}

function getStandardTenants() {
    return Auth.user?.tenants?.filter((tenant) => tenant !== 'SquaredUp Cloud Administrators') ?? [];
}
